import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg hover:shadow-xl hover:shadow-primary/25 interactive-button touch-target",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg hover:shadow-destructive/25 interactive-button touch-target",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent interactive-button touch-target",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-lg hover:shadow-xl hover:shadow-secondary/25 interactive-button touch-target",
        ghost: "hover:bg-accent hover:text-accent-foreground interactive-button touch-target",
        link: "text-primary underline-offset-4 hover:underline",
        // Noche Loco brand variants
        noche: "bg-gradient-to-r from-primary via-secondary to-accent text-white hover:shadow-xl hover:shadow-primary/30 cyber-button-glow interactive-button touch-target",
        "noche-outline": "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-white shadow-lg hover:shadow-primary/30 transition-all duration-300 interactive-button touch-target",
        "noche-ghost": "text-primary hover:bg-primary/10 hover:text-primary interactive-button touch-target",
        premium: "bg-gradient-to-r from-secondary via-primary to-accent text-white hover:shadow-xl hover:shadow-secondary/40 cyber-button-glow animate-gradient-shift interactive-button touch-target",
        glass: "glass-card hover:bg-glass-hover border-glass-border text-foreground font-medium interactive-button touch-target",
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-md px-8 text-base",
        xl: "h-14 rounded-lg px-10 text-lg font-semibold",
        icon: "h-10 w-10 p-0",
        "icon-sm": "h-8 w-8 p-0",
        "icon-lg": "h-12 w-12 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
